import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Type, Sliders, RefreshCw, Download, Search, Plus } from 'lucide-react';

interface TypographyConfig {
  fontFamilyPrimary: string;
  fontFamilyHeading: string;
  fontSizeBase: string;
  fontWeightNormal: string;
  fontWeightBold: string;
}

interface FontSelectorProps {
  typography: TypographyConfig;
  onChange: (typography: TypographyConfig) => void;
}

const FontSelector: React.FC<FontSelectorProps> = ({ typography, onChange }) => {
  const [customFontUrl, setCustomFontUrl] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showGoogleFonts, setShowGoogleFonts] = useState(false);
  const fontFamilies = [
    { name: 'Inter', value: "'Inter', sans-serif", category: 'Sans-serif' },
    { name: 'Roboto', value: "'Roboto', sans-serif", category: 'Sans-serif' },
    { name: 'Open Sans', value: "'Open Sans', sans-serif", category: 'Sans-serif' },
    { name: 'Lato', value: "'Lato', sans-serif", category: 'Sans-serif' },
    { name: 'Montserrat', value: "'Montserrat', sans-serif", category: 'Sans-serif' },
    { name: 'Poppins', value: "'Poppins', sans-serif", category: 'Sans-serif' },
    { name: 'Nunito', value: "'Nunito', sans-serif", category: 'Sans-serif' },
    { name: 'Source Sans Pro', value: "'Source Sans Pro', sans-serif", category: 'Sans-serif' },
    
    { name: 'Playfair Display', value: "'Playfair Display', serif", category: 'Serif' },
    { name: 'Merriweather', value: "'Merriweather', serif", category: 'Serif' },
    { name: 'Lora', value: "'Lora', serif", category: 'Serif' },
    { name: 'Crimson Text', value: "'Crimson Text', serif", category: 'Serif' },
    { name: 'EB Garamond', value: "'EB Garamond', serif", category: 'Serif' },
    { name: 'Libre Baskerville', value: "'Libre Baskerville', serif", category: 'Serif' },
    
    { name: 'Dancing Script', value: "'Dancing Script', cursive", category: 'Cursive' },
    { name: 'Pacifico', value: "'Pacifico', cursive", category: 'Cursive' },
    { name: 'Great Vibes', value: "'Great Vibes', cursive", category: 'Cursive' },
    { name: 'Satisfy', value: "'Satisfy', cursive", category: 'Cursive' },
    
    { name: 'Fira Code', value: "'Fira Code', monospace", category: 'Monospace' },
    { name: 'Source Code Pro', value: "'Source Code Pro', monospace", category: 'Monospace' },
    { name: 'JetBrains Mono', value: "'JetBrains Mono', monospace", category: 'Monospace' }
  ];

  const fontSizes = [
    { label: 'Muito Pequeno', value: '12px' },
    { label: 'Pequeno', value: '14px' },
    { label: 'Normal', value: '16px' },
    { label: 'Médio', value: '18px' },
    { label: 'Grande', value: '20px' },
    { label: 'Muito Grande', value: '24px' }
  ];

  const fontWeights = [
    { label: 'Fino', value: '300' },
    { label: 'Normal', value: '400' },
    { label: 'Médio', value: '500' },
    { label: 'Semi-negrito', value: '600' },
    { label: 'Negrito', value: '700' },
    { label: 'Extra-negrito', value: '800' }
  ];

  const typographyPresets = [
    {
      name: 'Moderno Elegante',
      config: {
        fontFamilyPrimary: "'Inter', sans-serif",
        fontFamilyHeading: "'Playfair Display', serif",
        fontSizeBase: '16px',
        fontWeightNormal: '400',
        fontWeightBold: '600'
      }
    },
    {
      name: 'Clássico Tradicional',
      config: {
        fontFamilyPrimary: "'Merriweather', serif",
        fontFamilyHeading: "'Playfair Display', serif",
        fontSizeBase: '16px',
        fontWeightNormal: '400',
        fontWeightBold: '700'
      }
    },
    {
      name: 'Minimalista Clean',
      config: {
        fontFamilyPrimary: "'Roboto', sans-serif",
        fontFamilyHeading: "'Montserrat', sans-serif",
        fontSizeBase: '15px',
        fontWeightNormal: '300',
        fontWeightBold: '500'
      }
    },
    {
      name: 'Criativo Artístico',
      config: {
        fontFamilyPrimary: "'Lato', sans-serif",
        fontFamilyHeading: "'Dancing Script', cursive",
        fontSizeBase: '16px',
        fontWeightNormal: '400',
        fontWeightBold: '600'
      }
    }
  ];

  const handleChange = (key: keyof TypographyConfig, value: string) => {
    onChange({
      ...typography,
      [key]: value
    });
  };

  const applyPreset = (preset: typeof typographyPresets[0]) => {
    onChange(preset.config);
  };

  const getFontName = (fontValue: string) => {
    const font = fontFamilies.find(f => f.value === fontValue);
    return font ? font.name : fontValue;
  };

  return (
    <div className="space-y-6">
      {/* Presets de Tipografia */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Type className="w-5 h-5 mr-2" />
          Estilos Predefinidos
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {typographyPresets.map((preset) => (
            <motion.div
              key={preset.name}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => applyPreset(preset)}
              className="cursor-pointer bg-white/5 rounded-lg p-4 border border-white/10 hover:border-white/30 transition-all"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-white">{preset.name}</h4>
                <RefreshCw className="w-4 h-4 text-purple-300" />
              </div>
              <div className="space-y-2">
                <div 
                  className="text-lg text-white"
                  style={{ 
                    fontFamily: preset.config.fontFamilyHeading,
                    fontWeight: preset.config.fontWeightBold
                  }}
                >
                  Título de Exemplo
                </div>
                <div 
                  className="text-sm text-purple-200"
                  style={{ 
                    fontFamily: preset.config.fontFamilyPrimary,
                    fontWeight: preset.config.fontWeightNormal
                  }}
                >
                  Texto de exemplo para demonstrar a tipografia
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Configurações Personalizadas */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Sliders className="w-5 h-5 mr-2" />
          Configurações Personalizadas
        </h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Fonte Principal */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Fonte Principal (Texto)
              </label>
              <select
                value={typography.fontFamilyPrimary}
                onChange={(e) => handleChange('fontFamilyPrimary', e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {fontFamilies.map((font) => (
                  <option key={font.value} value={font.value} className="bg-gray-800">
                    {font.name} ({font.category})
                  </option>
                ))}
              </select>
            </div>

            {/* Fonte dos Títulos */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Fonte dos Títulos
              </label>
              <select
                value={typography.fontFamilyHeading}
                onChange={(e) => handleChange('fontFamilyHeading', e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {fontFamilies.map((font) => (
                  <option key={font.value} value={font.value} className="bg-gray-800">
                    {font.name} ({font.category})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tamanhos e Pesos */}
          <div className="space-y-4">
            {/* Tamanho Base */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Tamanho Base da Fonte
              </label>
              <select
                value={typography.fontSizeBase}
                onChange={(e) => handleChange('fontSizeBase', e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {fontSizes.map((size) => (
                  <option key={size.value} value={size.value} className="bg-gray-800">
                    {size.label} ({size.value})
                  </option>
                ))}
              </select>
            </div>

            {/* Peso Normal */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Peso da Fonte Normal
              </label>
              <select
                value={typography.fontWeightNormal}
                onChange={(e) => handleChange('fontWeightNormal', e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {fontWeights.map((weight) => (
                  <option key={weight.value} value={weight.value} className="bg-gray-800">
                    {weight.label} ({weight.value})
                  </option>
                ))}
              </select>
            </div>

            {/* Peso Negrito */}
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                Peso da Fonte Negrito
              </label>
              <select
                value={typography.fontWeightBold}
                onChange={(e) => handleChange('fontWeightBold', e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {fontWeights.map((weight) => (
                  <option key={weight.value} value={weight.value} className="bg-gray-800">
                    {weight.label} ({weight.value})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Preview da Tipografia */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Preview da Tipografia</h3>
        <div className="bg-white/5 rounded-lg p-6 border border-white/20 space-y-4">
          <div 
            className="text-3xl text-white"
            style={{ 
              fontFamily: typography.fontFamilyHeading,
              fontWeight: typography.fontWeightBold,
              fontSize: `calc(${typography.fontSizeBase} * 2)`
            }}
          >
            Título Principal H1
          </div>
          
          <div 
            className="text-2xl text-white"
            style={{ 
              fontFamily: typography.fontFamilyHeading,
              fontWeight: typography.fontWeightBold,
              fontSize: `calc(${typography.fontSizeBase} * 1.5)`
            }}
          >
            Subtítulo H2
          </div>
          
          <div 
            className="text-white leading-relaxed"
            style={{ 
              fontFamily: typography.fontFamilyPrimary,
              fontWeight: typography.fontWeightNormal,
              fontSize: typography.fontSizeBase
            }}
          >
            Este é um parágrafo de exemplo usando a fonte principal selecionada. 
            Aqui você pode ver como o texto normal aparecerá no seu site. 
            <strong 
              style={{ 
                fontWeight: typography.fontWeightBold 
              }}
            >
              Este texto está em negrito
            </strong> para demonstrar o peso da fonte.
          </div>
          
          <div 
            className="text-purple-300"
            style={{ 
              fontFamily: typography.fontFamilyPrimary,
              fontWeight: typography.fontWeightNormal,
              fontSize: `calc(${typography.fontSizeBase} * 0.875)`
            }}
          >
            Texto secundário menor para legendas e informações adicionais.
          </div>
          
          <div className="pt-4 border-t border-white/10">
            <p className="text-sm text-purple-200 mb-2">Configuração Atual:</p>
            <div className="text-xs text-purple-300 space-y-1">
              <div>Fonte Principal: {getFontName(typography.fontFamilyPrimary)}</div>
              <div>Fonte Títulos: {getFontName(typography.fontFamilyHeading)}</div>
              <div>Tamanho Base: {typography.fontSizeBase}</div>
              <div>Peso Normal: {typography.fontWeightNormal} | Peso Negrito: {typography.fontWeightBold}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FontSelector;